import { FC, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { LngLatBounds } from "mapbox-gl";
import { FeatureCollection, Point } from "geojson";
import { ProviderMapResults } from "@app/projects/results/common/mapping";
import { outOfNetworkProviderNetworkTypes, ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { ZipNetworkProviderCard } from "@app/projects/zip-projects/maps/components/ZipNetworkProviderCard";
import { ZipImpactProviderCard } from "@app/projects/zip-projects/maps/components/ZipImpactProviderCard";
import {
  DEFAULT_PROVIDER_PAGE_SIZE,
  PROVIDER_PAGE_SIZE_OPTIONS,
} from "@app/projects/zip-projects/maps/ZipCoverageMapConstants";

type ProviderTab = "network" | "out-of-network";
type NetworkSortOption = "serves-desc" | "serves-asc" | "alpha-asc" | "alpha-desc";
type OutOfNetworkSortOption =
  | "impact-desc" | "impact-asc" | "gains-desc" | "gains-asc"
  | "serves-desc" | "serves-asc" | "alpha-asc" | "alpha-desc";

interface ZipCoverageMapProviderPanelProps {
  providerFeatures: FeatureCollection<Point, ProviderMapResults>;
  totalProviders: ProviderMapResults[];
  loadingProviders: boolean;
  providerFetchErrors: ProviderNetworkTypeEnum[];
  /** Null until the map settles once; the viewport filter is a no-op until then. */
  mapBounds: LngLatBounds | null;
  passPercentage: number | null;
  showNetworkTab: boolean;
  /** Hidden entirely, not just empty, when no Prospect/Market sources were applied. */
  showOutOfNetworkTab: boolean;
  selectedProvider: ProviderMapResults | null;
  onSelectProvider: (provider: ProviderMapResults | null) => void;
  isOpen: boolean;
  onToggleOpen: () => void;
}

// Menu order matches county's; the default selection is separate (serves-desc / impact-desc).
const NETWORK_SORT_OPTIONS: { value: NetworkSortOption; label: string }[] = [
  { label: "Name (A-Z)", value: "alpha-asc" },
  { label: "Name (Z-A)", value: "alpha-desc" },
  { label: "Serves % (High to Low)", value: "serves-desc" },
  { label: "Serves % (Low to High)", value: "serves-asc" },
];

const OUT_OF_NETWORK_SORT_OPTIONS: { value: OutOfNetworkSortOption; label: string }[] = [
  { label: "Name (A-Z)", value: "alpha-asc" },
  { label: "Name (Z-A)", value: "alpha-desc" },
  { label: "Serves % (High to Low)", value: "serves-desc" },
  { label: "Serves % (Low to High)", value: "serves-asc" },
  { label: "Gains % (High to Low)", value: "gains-desc" },
  { label: "Gains % (Low to High)", value: "gains-asc" },
  { label: "Impact % (High to Low)", value: "impact-desc" },
  { label: "Impact % (Low to High)", value: "impact-asc" },
];

const NETWORK_TAB_ID = "zip-coverage-map__provider-tab-network";
const OUT_OF_NETWORK_TAB_ID = "zip-coverage-map__provider-tab-out-of-network";
const PROVIDER_TABPANEL_ID = "zip-coverage-map__provider-tabpanel";

const byNameAsc = (a: ProviderMapResults, b: ProviderMapResults): number => a.displayName.localeCompare(b.displayName);

const sortNetworkProviders = (providers: ProviderMapResults[], sort: NetworkSortOption): ProviderMapResults[] => {
  const sorted = [...providers];
  switch (sort) {
    case "alpha-asc": return sorted.sort(byNameAsc);
    case "alpha-desc": return sorted.sort((a, b) => byNameAsc(b, a));
    case "serves-asc": return sorted.sort((a, b) => a.servesPercent - b.servesPercent);
    case "serves-desc": default: return sorted.sort((a, b) => b.servesPercent - a.servesPercent);
  }
};

const sortOutOfNetworkProviders = (providers: ProviderMapResults[], sort: OutOfNetworkSortOption): ProviderMapResults[] => {
  const sorted = [...providers];
  switch (sort) {
    case "alpha-asc": return sorted.sort(byNameAsc);
    case "alpha-desc": return sorted.sort((a, b) => byNameAsc(b, a));
    case "serves-asc": return sorted.sort((a, b) => a.servesPercent - b.servesPercent);
    case "serves-desc": return sorted.sort((a, b) => b.servesPercent - a.servesPercent);
    case "gains-asc": return sorted.sort((a, b) => (a.gainsPercent ?? 0) - (b.gainsPercent ?? 0));
    case "gains-desc": return sorted.sort((a, b) => (b.gainsPercent ?? 0) - (a.gainsPercent ?? 0));
    case "impact-asc": return sorted.sort((a, b) => (a.impactsPercent ?? 0) - (b.impactsPercent ?? 0));
    case "impact-desc": default: return sorted.sort((a, b) => (b.impactsPercent ?? 0) - (a.impactsPercent ?? 0));
  }
};

const matchesSearch = (provider: ProviderMapResults, searchText: string): boolean => {
  const term = searchText.trim().toLowerCase();
  return provider.displayName.toLowerCase().includes(term) || provider.npi.toLowerCase().includes(term);
};

/**
 * ZIP provider list panel (after Create): Network / Out-of-Network tabs, search, sort, pagination,
 * count, collapse, and empty states (202347). Prop-driven — consumes useZipCoverageMapData's
 * providerFeatures via ZipCoverageMapRoute rather than a cloned county-style Context.
 *
 * SOURCE (re-created, 199592): network-coverage-map/controls/ProviderAccordionControl.tsx
 * (+ Content). ZIP-owned. CONSOLIDATION (199592): a shared prop-driven ProviderAccordion needs
 * county's refactored to props; no shared/generic context.
 */
export const ZipCoverageMapProviderPanel: FC<ZipCoverageMapProviderPanelProps> = ({
  providerFeatures,
  totalProviders,
  loadingProviders,
  providerFetchErrors,
  mapBounds,
  passPercentage,
  showNetworkTab,
  showOutOfNetworkTab,
  selectedProvider,
  onSelectProvider,
  isOpen,
  onToggleOpen,
}) => {
  const [requestedTab, setRequestedTab] = useState<ProviderTab>(() => (showNetworkTab ? "network" : "out-of-network"));
  const requestedTabIsVisible = requestedTab === "network" ? showNetworkTab : showOutOfNetworkTab;
  const selectedTab: ProviderTab = requestedTabIsVisible
    ? requestedTab
    : (showNetworkTab ? "network" : "out-of-network");
  const [searchText, setSearchText] = useState("");
  const [networkSort, setNetworkSort] = useState<NetworkSortOption>("serves-desc");
  const [outOfNetworkSort, setOutOfNetworkSort] = useState<OutOfNetworkSortOption>("impact-desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PROVIDER_PAGE_SIZE);

  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const boundsAtSelectionRef = useRef<LngLatBounds | null>(null);
  const scrolledProviderIdRef = useRef<string | null>(null);

  // isOpen/onToggleOpen are omitted from the deps: the parent passes fresh callbacks every render, so
  // including them would re-run this on unrelated renders.
  useEffect(() => {
    if (!selectedProvider) return;
    setRequestedTab(selectedProvider.providerNetworkType === ProviderNetworkTypeEnum.Network ? "network" : "out-of-network");
    if (!isOpen) onToggleOpen();
    // A search term hiding the pin's provider would leave no card to select.
    if (searchText.trim() && !matchesSearch(selectedProvider, searchText)) setSearchText("");
  }, [selectedProvider]);

  const allProviders = useMemo(
    () => providerFeatures.features.map((feature) => feature.properties),
    [providerFeatures],
  );

  const isOnSelectedTab = (provider: ProviderMapResults): boolean => (selectedTab === "network"
    ? provider.providerNetworkType === ProviderNetworkTypeEnum.Network
    : outOfNetworkProviderNetworkTypes.includes(provider.providerNetworkType));

  const tabProviders = useMemo(
    () => allProviders.filter(isOnSelectedTab),
    [allProviders, selectedTab],
  );

  const totalTabProviderCount = useMemo(
    () => totalProviders.filter(isOnSelectedTab).length,
    [totalProviders, selectedTab],
  );

  const viewportProviders = useMemo(
    () => (mapBounds ? tabProviders.filter((p) => mapBounds.contains([p.longitude, p.latitude])) : tabProviders),
    [tabProviders, mapBounds],
  );

  const searchedProviders = useMemo(
    () => (searchText.trim() ? viewportProviders.filter((p) => matchesSearch(p, searchText)) : viewportProviders),
    [viewportProviders, searchText],
  );

  const sortedProviders = useMemo(
    () => (selectedTab === "network"
      ? sortNetworkProviders(searchedProviders, networkSort)
      : sortOutOfNetworkProviders(searchedProviders, outOfNetworkSort)),
    [searchedProviders, selectedTab, networkSort, outOfNetworkSort],
  );

  const totalPages = Math.max(1, Math.ceil(sortedProviders.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedProviders = sortedProviders.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Keeps a stale page number from pointing past the end of a narrowed result set.
  useEffect(() => {
    setPage(1);
  }, [pageSize, selectedTab, searchText, mapBounds, providerFeatures]);

  const selectedIndex = selectedProvider
    ? sortedProviders.findIndex((p) => p.providerUniqueId === selectedProvider.providerUniqueId)
    : -1;

  // Declared after the reset above so a pin that also switches tabs lands on the provider's page
  // rather than page 1.
  useEffect(() => {
    if (selectedIndex < 0) return;
    setPage(Math.floor(selectedIndex / pageSize) + 1);
  }, [selectedIndex, pageSize, mapBounds]);

  // A new selection starts a pan; remember the view it started from so the scroll below can wait for the
  // list to be re-filtered when the map stops moving.
  useEffect(() => {
    boundsAtSelectionRef.current = mapBounds;
    scrolledProviderIdRef.current = null;
  }, [selectedProvider?.providerUniqueId]);

  // Depends on pagedProviders so it runs after the page jump above renders the card. Waits for the first bounds
  // update after the selection; only the first scroll per selection animates. jsdom has no scrollIntoView.
  useEffect(() => {
    if (!selectedProvider) return;
    if (mapBounds === boundsAtSelectionRef.current) return;

    const card = cardRefs.current[selectedProvider.providerUniqueId];
    if (!card) return;

    const isFirstScroll = scrolledProviderIdRef.current !== selectedProvider.providerUniqueId;
    scrolledProviderIdRef.current = selectedProvider.providerUniqueId;
    card.scrollIntoView?.({ behavior: isFirstScroll ? "smooth" : "auto", block: "nearest" });
  }, [selectedProvider, pagedProviders]);

  const relevantFetchErrors = selectedTab === "network"
    ? providerFetchErrors.filter((source) => source === ProviderNetworkTypeEnum.Network)
    : providerFetchErrors.filter((source) => source === ProviderNetworkTypeEnum.Prospect || source === ProviderNetworkTypeEnum.Market);

  const hasResultsToPaginate = !loadingProviders && relevantFetchErrors.length === 0 && sortedProviders.length > 0;

  return (
    <div
      className={classNames("zip-coverage-map__provider-accordion", {
        "zip-coverage-map__provider-accordion--collapsed": !isOpen,
      })}
    >
      {/* Outside the scrolling content below so it stays put and unclipped. */}
      <QESButton
        className="zip-coverage-map__provider-accordion-button"
        onClick={onToggleOpen}
        qesTestId="zip-coverage-map__provider-accordion-button"
        qesType="secondary"
      >
        <TooltipWrapper
          key={isOpen ? "provider-accordion-open-tooltip" : "provider-accordion-closed-tooltip"}
          text={isOpen ? "Close Providers" : "Open Providers"}
        >
          <QesIcon
            iconName={QesIcons.ChevronRight}
            color="#828282"
            description={isOpen ? "Close Providers" : "Open Providers"}
            height="16"
            style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
            width="16"
          />
        </TooltipWrapper>
      </QESButton>

      {isOpen && (
        <div className="zip-coverage-map__provider-accordion-content">
          <div className="tabs-style-2 zip-coverage-map__provider-tabs">
            <ul className="nav nav-tabs nav-fill" role="tablist">
              {showNetworkTab && (
                <li className={classNames("nav-item", { "w-100 text-center": !showOutOfNetworkTab })} role="none">
                  <button
                    type="button"
                    id={NETWORK_TAB_ID}
                    className={`nav-link ${selectedTab === "network" ? "active" : ""}`}
                    onClick={() => setRequestedTab("network")}
                    role="tab"
                    aria-selected={selectedTab === "network"}
                    aria-controls={PROVIDER_TABPANEL_ID}
                  >
                    Network Providers
                  </button>
                </li>
              )}
              {showOutOfNetworkTab && (
                <li className={classNames("nav-item", { "w-100 text-center": !showNetworkTab })} role="none">
                  <button
                    type="button"
                    id={OUT_OF_NETWORK_TAB_ID}
                    className={`nav-link ${selectedTab === "out-of-network" ? "active" : ""}`}
                    onClick={() => setRequestedTab("out-of-network")}
                    role="tab"
                    aria-selected={selectedTab === "out-of-network"}
                    aria-controls={PROVIDER_TABPANEL_ID}
                  >
                    Out-of-Network Providers
                    <TooltipWrapper
                      text="These providers service the area but are not currently in your network"
                      position="right"
                      containerClassName="ms-2"
                    >
                      {/* A description here would be appended to the tab's accessible name. */}
                      <QesIcon iconName={QesIcons.InfoCircle} color="#828282" height="16px" width="16px" isDecorative />
                    </TooltipWrapper>
                  </button>
                </li>
              )}
            </ul>
          </div>

          <div className="mt-3 zip-coverage-map__provider-search">
            <QESTextInput
              label="Search"
              hideLabel
              placeholder="Search by name or NPI"
              testId="zip-coverage-map__provider-accordion-search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              inputAppend={<QesIcon iconName={QesIcons.SearchSm} isDecorative height="15" />}
            />
          </div>

          <div className="zip-coverage-map__provider-sort-row">
            <NewActionMenu
              buttonClassNames="qes-button-secondary"
              containerClassnames="zip-coverage-map__provider-sort-container"
              buttonText={
                <>
                  <QesIcon iconName={QesIcons.SwitchVerticalV1} isDecorative />&nbsp;
                  <span>Sort by</span>
                </>
              }
              testId="zip-coverage-map__provider-accordion-sort"
              hideArrow
            >
              {(selectedTab === "network" ? NETWORK_SORT_OPTIONS : OUT_OF_NETWORK_SORT_OPTIONS).map((option) => (
                <NewActionMenuItem
                  display={option.label}
                  key={option.value}
                  onClick={() => (selectedTab === "network"
                    ? setNetworkSort(option.value as NetworkSortOption)
                    : setOutOfNetworkSort(option.value as OutOfNetworkSortOption))}
                  isSelected={(selectedTab === "network" ? networkSort : outOfNetworkSort) === option.value}
                  testId={`zip-coverage-map__provider-accordion-sort-${option.value}`}
                />
              ))}
            </NewActionMenu>
            <div className="label-tiny zip-coverage-map__provider-count">
              Providers: {searchedProviders.length} of {totalTabProviderCount}
              <TooltipWrapper
                text="Displays the number of providers that match your current filters and view out of the total available"
                position="right"
                containerClassName="ms-2"
                tooltipWidth="20rem"
              >
                <QesIcon
                  iconName={QesIcons.InfoCircle}
                  color="#828282"
                  height="16px"
                  width="16px"
                  description="Tooltip"
                  className="mb-1"
                />
              </TooltipWrapper>
            </div>
          </div>

          <div
            className="zip-coverage-map__provider-list"
            id={PROVIDER_TABPANEL_ID}
            role="tabpanel"
            aria-labelledby={selectedTab === "network" ? NETWORK_TAB_ID : OUT_OF_NETWORK_TAB_ID}
          >
            {loadingProviders ? (
              <div className="zip-coverage-map__provider-loading">Loading providers...</div>
            ) : relevantFetchErrors.length > 0 ? (
              <QESAlert type="danger" persist>
                There was an error loading {selectedTab === "network" ? "Network" : "Prospect & Market"} providers
              </QESAlert>
            ) : tabProviders.length === 0 ? (
              <div className="zip-coverage-map__provider-empty">
                There are no providers available for the selected zip-specialty group combination
              </div>
            ) : sortedProviders.length === 0 ? (
              <div className="zip-coverage-map__provider-empty">
                There are more providers available. Consider adjusting your search or recentering the map to view additional provider options
              </div>
            ) : (
              pagedProviders.map((provider) => (
                <div
                  key={provider.providerUniqueId}
                  ref={(node) => { cardRefs.current[provider.providerUniqueId] = node; }}
                >
                  {selectedTab === "network" ? (
                    <ZipNetworkProviderCard
                      provider={provider}
                      isSelected={provider.providerUniqueId === selectedProvider?.providerUniqueId}
                      onClick={onSelectProvider}
                    />
                  ) : (
                    <ZipImpactProviderCard
                      provider={provider}
                      isSelected={provider.providerUniqueId === selectedProvider?.providerUniqueId}
                      passPercentage={passPercentage}
                      onClick={onSelectProvider}
                    />
                  )}
                </div>
              ))
            )}
          </div>

          {hasResultsToPaginate && (
            <div className="zip-coverage-map__provider-pagination">
              <div className="d-flex justify-content-between align-items-center mt-2">
                <QESButton
                  qesType="secondary"
                  disabled={currentPage <= 1}
                  onClick={() => setPage(currentPage - 1)}
                  qesTestId="zip-coverage-map__provider-prev-page-button"
                >
                  Previous
                </QESButton>
                <span className="zip-coverage-map__page-of">Page {currentPage} of {totalPages}</span>
                <QESButton
                  qesType="secondary"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage(currentPage + 1)}
                  qesTestId="zip-coverage-map__provider-next-page-button"
                >
                  Next
                </QESButton>
              </div>
              <div className="mt-0 d-flex justify-content-center">
                <QESSelect
                  id="zip-provider-page-size-select"
                  label="Page Size"
                  hideLabel
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  testId="zip-coverage-map__provider-page-size-select"
                >
                  {PROVIDER_PAGE_SIZE_OPTIONS.map((size) => <option key={size} value={size}>{size}</option>)}
                </QESSelect>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ZipCoverageMapProviderPanel;
